import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi, profileApi } from '../api/api';
import {
  User,
  AuthContextValue,
  LoginResult,
  RegisterResult,
  RegisterData,
} from '../types';

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load user from localStorage on mount
  useEffect(() => {
    const token = localStorage.getItem('leaguePortalToken');
    const savedUser = localStorage.getItem('leaguePortalUser');

    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser) as User);
        // Verify token is still valid
        profileApi.get()
          .then(response => {
            setUser(response.data.user);
            localStorage.setItem('leaguePortalUser', JSON.stringify(response.data.user));
          })
          .catch(() => {
            // Token invalid, clear storage
            localStorage.removeItem('leaguePortalToken');
            localStorage.removeItem('leaguePortalUser');
            setUser(null);
          });
      } catch {
        localStorage.removeItem('leaguePortalUser');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email: string, password: string): Promise<LoginResult> => {
    try {
      setError(null);
      const response = await authApi.login({ email, password });
      const { token, user: userData } = response.data;

      localStorage.setItem('leaguePortalToken', token);
      localStorage.setItem('leaguePortalUser', JSON.stringify(userData));
      setUser(userData);

      return { success: true };
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      const message = error.response?.data?.message || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const register = async (data: RegisterData): Promise<RegisterResult> => {
    try {
      setError(null);
      const response = await authApi.register(data);
      return { success: true, message: response.data.message };
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      const message = error.response?.data?.message || 'Registration failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch {
      // Ignore logout errors
    }
    localStorage.removeItem('leaguePortalToken');
    localStorage.removeItem('leaguePortalUser');
    setUser(null);
  };

  const updateUser = (userData: User): void => {
    setUser(userData);
    localStorage.setItem('leaguePortalUser', JSON.stringify(userData));
  };

  const clearError = (): void => {
    setError(null);
  };

  const value: AuthContextValue = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    updateUser,
    clearError,
    isAuthenticated: !!user,
    isVerified: user?.isVerified || false,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
